import React from 'react';
import styled from '@emotion/styled';
import {
    boxSizes,
    colors,
    fontFamilies,
    fontSizes,
    fontWeights,
    gutterSizes,
    lineHeights,
} from '@gumtree/ui-library/src/base/theme';

interface DiscountTipsData {
    text: string;
    id: string;
    maxDisplayCount: number;
    duration: number;
    minHour: number;
}

interface DiscountTipsResponse {
    discountTips?: DiscountTipsData | null;
}

const VIEWPORT_GUTTER = 16;
const DISCOUNT_TIPS_HISTORY_KEY = 'gumtree-discount-tips-history';
const DISCOUNT_TIPS_REQUESTED_KEY = 'gumtree-discount-tips-requested';

interface DiscountTipsHistoryEntry {
    count: number;
    lastDisplay: number;
}

const readObject = <T extends object>(key: string, fallback: T): T => {
    try {
        const value = window.localStorage.getItem(key);
        const parsed = value ? JSON.parse(value) : fallback;

        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : fallback;
    } catch {
        return fallback;
    }
};

const writeObject = (key: string, value: object) => {
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // The bubble remains optional when storage is unavailable.
    }
};

const claimTabRequest = () => {
    try {
        if (window.sessionStorage.getItem(DISCOUNT_TIPS_REQUESTED_KEY)) return false;

        window.sessionStorage.setItem(DISCOUNT_TIPS_REQUESTED_KEY, 'true');
    } catch {
        // Continue when sessionStorage is unavailable.
    }

    return true;
};

const isValidTips = (tips?: DiscountTipsData | null): tips is DiscountTipsData =>
    Boolean(
        tips?.text?.trim() &&
            tips.id?.trim() &&
            Number.isInteger(tips.maxDisplayCount) &&
            tips.maxDisplayCount > 0 &&
            Number.isInteger(tips.duration) &&
            tips.duration > 0 &&
            Number.isFinite(tips.minHour) &&
            tips.minHour >= 0
    );

const Wrapper = styled.span<{ active: boolean }>`
    position: relative;
    display: ${({ active }) => (active ? 'inline-flex' : 'contents')};

    .discount-tips-bubble {
        position: absolute;
        z-index: 2;
        top: calc(100% + ${gutterSizes.large} - ${gutterSizes.xsmall});
        left: 50%;
        transform: translateX(calc(-50% + var(--discount-tips-shift, 0px)));
        width: max-content;
        max-width: min(280px, calc(100vw - 32px));
        box-sizing: border-box;
        padding: ${gutterSizes.base} ${gutterSizes.medium};
        border-radius: ${boxSizes.borderRadius8};
        background: ${colors.votNavigationOverlay};
        color: ${colors.white};
        font-family: ${fontFamilies.readexPro};
        font-size: ${fontSizes.small14};
        font-weight: ${fontWeights.normal};
        line-height: ${lineHeights.small};
        white-space: normal;
        overflow-wrap: anywhere;
        pointer-events: none;
    }

    .discount-tips-arrow {
        position: absolute;
        bottom: 100%;
        left: calc(50% - var(--discount-tips-shift, 0px));
        width: 14px;
        height: 9px;
        color: ${colors.votNavigationOverlay};
        transform: translateX(-50%);
    }

    .discount-tips-arrow path {
        fill: currentColor;
    }
`;

interface Props {
    enabled: boolean;
    mobile?: boolean;
    children: React.ReactNode;
}

export default function DiscountTips({ enabled, mobile, children }: Readonly<Props>) {
    const [tips, setTips] = React.useState<DiscountTipsData | null>(null);
    const wrapperRef = React.useRef<HTMLSpanElement>(null);
    const bubbleRef = React.useRef<HTMLOutputElement>(null);
    const visible = Boolean(tips?.text?.trim());

    React.useEffect(() => {
        if (!enabled || typeof window === 'undefined') {
            setTips(null);
            return undefined;
        }

        if (!claimTabRequest()) {
            setTips(null);
            return undefined;
        }

        const controller = new AbortController();
        let timeout: number | undefined;

        const loadTips = async () => {
            try {
                const response = await fetch('/bff-api/discount-tips', {
                    credentials: 'same-origin',
                    headers: { accept: 'application/json' },
                    signal: controller.signal,
                });

                if (!response.ok) {
                    setTips(null);
                    return;
                }

                const { discountTips } = (await response.json()) as DiscountTipsResponse;
                if (!isValidTips(discountTips)) {
                    setTips(null);
                    return;
                }

                const history = readObject<Record<string, DiscountTipsHistoryEntry>>(
                    DISCOUNT_TIPS_HISTORY_KEY,
                    {}
                );
                const storedIds = Object.keys(history);
                const idChanged = storedIds.length !== 1 || storedIds[0] !== discountTips.id;
                const currentHistory = idChanged ? undefined : history[discountTips.id];
                const count = Number.isFinite(currentHistory?.count)
                    ? Math.max(0, currentHistory?.count || 0)
                    : 0;
                const now = Date.now();
                const elapsedHours = Number.isFinite(currentHistory?.lastDisplay)
                    ? (now - (currentHistory?.lastDisplay || 0)) / 3600000
                    : Infinity;

                if (count >= discountTips.maxDisplayCount || elapsedHours < discountTips.minHour) {
                    setTips(null);
                    return;
                }

                writeObject(DISCOUNT_TIPS_HISTORY_KEY, {
                    [discountTips.id]: { count: count + 1, lastDisplay: now },
                });
                setTips(discountTips);
                timeout = window.setTimeout(() => setTips(null), discountTips.duration * 1000);
            } catch {
                if (!controller.signal.aborted) setTips(null);
            }
        };

        void loadTips();

        return () => {
            controller.abort();
            if (timeout !== undefined) window.clearTimeout(timeout);
        };
    }, [enabled]);

    React.useEffect(() => {
        if (!visible || typeof window === 'undefined') return undefined;

        const keepBubbleInViewport = () => {
            const bubble = bubbleRef.current;
            if (!bubble) return;

            bubble.style.setProperty('--discount-tips-shift', '0px');
            const { left, right } = bubble.getBoundingClientRect();
            const navRect = mobile
                ? wrapperRef.current?.closest('nav')?.getBoundingClientRect()
                : undefined;
            const hasNavBounds = Boolean(navRect?.width);
            const leftBoundary = Math.max(
                VIEWPORT_GUTTER,
                hasNavBounds ? (navRect?.left || 0) + VIEWPORT_GUTTER : VIEWPORT_GUTTER
            );
            const rightBoundary = Math.min(
                window.innerWidth - VIEWPORT_GUTTER,
                hasNavBounds
                    ? (navRect?.right || window.innerWidth) - VIEWPORT_GUTTER
                    : window.innerWidth - VIEWPORT_GUTTER
            );
            let shift = 0;
            if (left < leftBoundary) {
                shift = leftBoundary - left;
            } else if (right > rightBoundary) {
                shift = rightBoundary - right;
            }

            bubble.style.setProperty('--discount-tips-shift', `${shift}px`);
        };

        keepBubbleInViewport();
        window.addEventListener('resize', keepBubbleInViewport);

        return () => window.removeEventListener('resize', keepBubbleInViewport);
    }, [mobile, visible, tips?.text]);

    if (!enabled) return <>{children}</>;

    return (
        <Wrapper ref={wrapperRef} active={visible}>
            {children}
            {visible && (
                <output
                    ref={bubbleRef}
                    className="discount-tips-bubble"
                    data-testid="discount-tips"
                >
                    <svg
                        className="discount-tips-arrow"
                        viewBox="0 0 14 9"
                        aria-hidden="true"
                        focusable="false"
                    >
                        <path d="M0 9C1.7 9 2.3 8.3 3 7.1L6.1 1.2Q7 0 7.9 1.2L11 7.1C11.7 8.3 12.3 9 14 9H0Z" />
                    </svg>
                    {tips?.text}
                </output>
            )}
        </Wrapper>
    );
}
